/**
 * A card grid that picks its column count from the space it actually
 * has, never from a number decided somewhere else.
 *
 * WHY THIS IS A COMPONENT AND NOT A STYLE
 *
 * The authored ``card_columns`` says how many cards the author wants
 * side by side AT FULL WIDTH. It is an intent, not a measurement, and
 * the same block is now rendered in containers the author never saw: a
 * Home-board tile the user drags to any width, a mobile stack, a
 * builder preview. Treating that intent as an absolute produced the
 * failure measured on staging 2026-08-04 — four country cards forced
 * into 139px each until their names rendered as "U." / "C." / "M.".
 *
 * A CSS-only ``auto-fill`` cannot express the rule we need, because the
 * authored count is a CEILING and not a target: at 1290px an author who
 * asked for 2 must still get 2, not 4. So the component measures, and
 * the rule is one line you can read:
 *
 *     columns = clamp(1, floor(width / minCardPx), authoredMax)
 *
 * Below ``minCardPx`` a card stops being readable, so the grid drops a
 * column instead of shrinking past it. Content is never hidden and
 * never truncated to make it fit — that is the BI rule ("somos BI, no
 * cortamos data"); the grid simply gets taller and its host scrolls.
 *
 * Reusable by construction: any card-shaped block (scorecards, KPI
 * groups, tile rows) gets the same behaviour by rendering through this
 * component instead of hand-rolling a ``repeat(n, …)`` template.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

/** Below this a card cannot hold a label plus its value. Measured
 *  against the longest real labels in the fleet ("MERCADOLIBRE" needs
 *  93px next to a meter and a value). */
export const MIN_CARD_PX = 260;

export interface ResponsiveCardGridProps {
  /** The authored ceiling (``card_columns``). Absent = no ceiling
   *  beyond what fits. */
  maxColumns?: number;
  /** Width below which a card is no longer readable. */
  minCardPx?: number;
  /** Grid gap in px — also used in the fit calculation, so the count is
   *  right instead of one-off. */
  gapPx?: number;
  /** Equal-height rows (cards in a row share the tallest height). */
  equalRows?: boolean;
  className?: string;
  children: ReactNode;
}

export function ResponsiveCardGrid({
  maxColumns,
  minCardPx = MIN_CARD_PX,
  gapPx = 12,
  equalRows = true,
  className,
  children,
}: ResponsiveCardGridProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  // Start at the authored ceiling: the first paint of a full-width
  // dashboard is then already correct, and only a NARROW container
  // needs the post-measure correction.
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === "number" && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ceiling = Math.max(1, maxColumns ?? Number.MAX_SAFE_INTEGER);
  // Before the first measurement, honour the author (dashboards are
  // wide; a flash of too-few columns is worse than one of too many).
  const fits =
    width === null
      ? ceiling
      : Math.max(1, Math.floor((width + gapPx) / (minCardPx + gapPx)));
  const columns = Math.max(1, Math.min(ceiling, fits));

  return (
    <div
      ref={ref}
      data-columns={columns}
      className={className}
      style={{
        display: "grid",
        gap: `${gapPx}px`,
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        ...(equalRows ? { gridAutoRows: "1fr" } : {}),
      }}
    >
      {children}
    </div>
  );
}
